import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
};

export default function TempEquipmentModal({
  isOpen,
  onClose,
  serviceName,
  initialItems = [], // [{ id, name, qty, unit, remark }]
  onSave,
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setItems(
        initialItems.length > 0
          ? initialItems.map((it) => ({ ...it }))
          : [{ id: uuid(), name: "", qty: "", unit: "", remark: "" }]
      );
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const addRow = () =>
    setItems((prev) => [...prev, { id: uuid(), name: "", qty: "", unit: "", remark: "" }]);

  const removeRow = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const change = (id, field, value) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));

  const handleSave = () => {
    const valid = items.filter((it) => it.name?.trim());
    onSave(valid);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-black/40 flex justify-center items-center">
      <div className="bg-white w-auto min-w-[700px] max-w-[95vw] max-h-[85vh] rounded-xl shadow-xl overflow-hidden flex flex-col">

        {/* HEADER */}
        <div className="p-3 bg-[#D97706] text-white flex justify-between items-center flex-shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Icon icon="mdi:toolbox-outline" className="w-5 h-5" />
            อุปกรณ์ชั่วคราว{serviceName ? `: ${serviceName}` : ""}
          </h3>
          <button onClick={onClose} className="text-2xl px-2 leading-none">×</button>
        </div>

        {/* BODY */}
        <div className="p-4 overflow-y-auto flex-1">
          <table className="w-full text-[13px] border border-gray-300 rounded-lg overflow-hidden">
            <thead>
              <tr>
                <th className="bg-[#D97706] text-white text-left border border-gray-300 px-2 py-2 w-[35%]">
                  ชื่ออุปกรณ์ / รายการ
                </th>
                <th className="bg-[#D97706] text-white text-center border border-gray-300 px-2 py-2 w-[12%]">
                  จำนวน
                </th>
                <th className="bg-[#D97706] text-white text-center border border-gray-300 px-2 py-2 w-[15%]">
                  หน่วย
                </th>
                <th className="bg-[#D97706] text-white text-left border border-gray-300 px-2 py-2">
                  หมายเหตุ
                </th>
                <th className="bg-[#D97706] text-white text-center border border-gray-300 px-2 py-2 w-[5%]">
                  ลบ
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className={idx % 2 === 0 ? "bg-white" : "bg-amber-50"}>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="text"
                      value={it.name}
                      onChange={(e) => change(it.id, "name", e.target.value)}
                      placeholder="ชื่ออุปกรณ์"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-[13px] focus:ring-1 focus:ring-[#D97706] focus:border-[#D97706]"
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      value={it.qty}
                      onChange={(e) => change(it.id, "qty", e.target.value)}
                      placeholder="0"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-[13px] text-center focus:ring-1 focus:ring-[#D97706] focus:border-[#D97706]"
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="text"
                      value={it.unit}
                      onChange={(e) => change(it.id, "unit", e.target.value)}
                      placeholder="หน่วย"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-[13px] text-center focus:ring-1 focus:ring-[#D97706] focus:border-[#D97706]"
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="text"
                      value={it.remark}
                      onChange={(e) => change(it.id, "remark", e.target.value)}
                      placeholder="หมายเหตุ"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-[13px] focus:ring-1 focus:ring-[#D97706] focus:border-[#D97706]"
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(it.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Icon icon="mdi:delete" className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            type="button"
            onClick={addRow}
            className="mt-3 flex items-center gap-1 text-[#D97706] hover:text-[#b45309] text-[13px] font-medium"
          >
            <Icon icon="mdi:plus-circle-outline" className="w-4 h-4" />
            เพิ่มรายการ
          </button>
        </div>

        {/* FOOTER */}
        <div className="p-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-[13px]"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-[#D97706] hover:bg-[#b45309] text-white rounded-md text-[13px]"
          >
            บันทึก
          </button>
        </div>
      </div>
    </div>
  );
}
